import React, { createContext, useState, useEffect, useContext, ReactNode, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { useSocket } from './SocketContext';
import { useAuth } from './AuthContext';

interface Mercenary {
  id: number;
  name: string;
  level: number;
  race: string;
  sex: string;
  avatar: number;
}

interface MercenaryContextProps {
  mercenaries: any[];
  mercenary: any | null;
  mercenaryId: number | null;
  fetchUserMercenaries: () => Promise<{ mercenaries: Mercenary[]; canCreate: boolean; isPremiumOrHigher: boolean; dailyLimitReached?: boolean; nextAvailableAt?: string | null; dailyLimit?: number | null } | undefined>;
  switchMercenary: (mercenary: any) => void;
  createMercenary: (mercenaryData: any) => Promise<void>;
  deleteMercenary: (mercenaryId: number) => Promise<void>;
  setMercenary: React.Dispatch<React.SetStateAction<any | null>>;
  setMercenaryId: (id: number | null) => void;
}

const MercenaryContext = createContext<MercenaryContextProps | undefined>(undefined);

interface MercenaryProviderProps {
  children: ReactNode;
}

export const MercenaryProvider: React.FC<MercenaryProviderProps> = ({ children }) => {
  const [mercenaries, setMercenaries] = useState<any[]>([]);
  const [mercenary, setMercenary] = useState<any | null>(null);
  const [mercenaryId, setMercenaryId] = useState<number | null>(() => {
    const storedId = localStorage.getItem('mercenaryId');
    return storedId ? parseInt(storedId, 10) : null;
  });
  const { socket } = useSocket();
  const { user, setUser } = useAuth();

  useEffect(() => {
    if (mercenaryId !== null) {
      localStorage.setItem('mercenaryId', mercenaryId.toString());
    } else {
      localStorage.removeItem('mercenaryId');
    }
  }, [mercenaryId]);

  // Re-fetch (or clear) all mercenaries whenever the logged-in user changes.
  // This ensures the header quick-swap list always belongs to the current account —
  // not a stale list left over from a previous session in the same tab.
  useEffect(() => {
    if (!user) {
      // User logged out, wipe the list and the active selection immediately.
      setMercenaries([]);
      setMercenary(null);
      return;
    }
    const load = async () => {
      try {
        const response = await axios.get('/api/mercenary/all', { withCredentials: true });
        setMercenaries(response.data.mercenaries ?? []);
      } catch {}
    };
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Listen to socket updates and sync mercenary state
  useEffect(() => {
    if (socket && mercenaryId) {
      const handleMercenaryUpdate = (data: { mercenaryId: number; updatedStats: any }) => {
        const { mercenaryId: updatedMercenaryId, updatedStats } = data;
        if (updatedMercenaryId === mercenaryId) {
          setMercenary((prevState: any | null) => {
            if (!prevState) return prevState;
            return { ...prevState, ...updatedStats };
          });
        }
        // Also update the mercenaries list so header quick-swap reflects changes
        // (e.g. skill points after a level-up on an alt)
        setMercenaries(prev => prev.map(m =>
          m.id === updatedMercenaryId
            ? { ...m, ...updatedStats }
            : m
        ));
      };

      const handleMercenaryRegen = (data: { mercenaryId: number; regen: { hp: number; rounds: number; maxHp?: number; maxRounds?: number; restedExp?: number } }) => {
        const { mercenaryId: regenMercenaryId, regen } = data;
        if (regenMercenaryId === mercenaryId) {
          setMercenary((prevState: any | null) => {
            if (!prevState) return prevState;
            const updates: any = { health: regen.hp, effectiveHealth: regen.hp, rounds: regen.rounds };
            if (regen.restedExp !== undefined) updates.restedExp = regen.restedExp;
            if (regen.maxHp !== undefined) {
              updates.maxHealth = regen.maxHp;
              updates.effectiveMaxHealth = regen.maxHp;
            }
            if (regen.maxRounds !== undefined) updates.maxRounds = regen.maxRounds;
            return { ...prevState, ...updates };
          });
        }
      };

      // Fired when this mercenary is deleted/retired on another device/tab
      const handleMercenaryRetired = ({ mercenaryId: retiredId }: { mercenaryId: number }) => {
        if (retiredId === mercenaryId) {
          setMercenary(null);
          setMercenaryId(null);
          setMercenaries(prev => prev.filter(m => m.id !== retiredId));
        }
      };

      socket.on('update_mercenary_stats', handleMercenaryUpdate);
      socket.on('mercenary_regen', handleMercenaryRegen);
      socket.on('mercenary_retired', handleMercenaryRetired);

      return () => {
        socket.off('update_mercenary_stats', handleMercenaryUpdate);
        socket.off('mercenary_regen', handleMercenaryRegen);
        socket.off('mercenary_retired', handleMercenaryRetired);
      };
    }
  }, [socket, mercenaryId]);

  // Axios response interceptor: auto-apply updatedStats from any API response
  // so frontend stat updates don't rely solely on unreliable socket events
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use((response) => {
      if (response.data?.updatedStats && mercenaryId) {
        setMercenary((prev: any) => prev ? { ...prev, ...response.data.updatedStats } : prev);
      }
      return response;
    });
    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, [mercenaryId]);

  const fetchUserMercenaries = useCallback(async (): Promise<{ mercenaries: Mercenary[]; canCreate: boolean; isPremiumOrHigher: boolean; dailyLimitReached?: boolean; nextAvailableAt?: string | null; dailyLimit?: number | null } | undefined> => {
    try {
      const response = await axios.get('/api/mercenary/all', { withCredentials: true });
      const data = response.data;
      setMercenaries(data.mercenaries);
      return {
        mercenaries: data.mercenaries,
        canCreate: data.canCreate,
        isPremiumOrHigher: data.isPremiumOrHigher,
        dailyLimitReached: data.dailyLimitReached ?? false,
        nextAvailableAt: data.nextAvailableAt ?? null,
        dailyLimit: data.dailyLimit ?? null,
      };
    } catch (error) {
      console.error('Failed to fetch mercenaries:', error);
      return undefined;
    }
  }, []);

  const switchingRef = useRef(false);

  const switchMercenary = useCallback(async (selectedMercenary: Mercenary) => {
    // Prevent concurrent switches, rapid clicks or cascading re-renders
    // could fire overlapping calls that toggle mercenaries back and forth.
    if (switchingRef.current) return;
    switchingRef.current = true;
    try {
      setMercenaryId(selectedMercenary.id);
      const response = await axios.get(`/api/mercenary/set-active/${selectedMercenary.id}`, { withCredentials: true });
      const fullMercenaryData = response.data;
      setMercenary(fullMercenaryData);
      // Refresh the full mercenaries list so header badges (skill point pulse, etc.)
      // are always up-to-date after switching.
      const listRes = await axios.get('/api/mercenary/all', { withCredentials: true });
      setMercenaries(listRes.data.mercenaries ?? []);
      // If the user was dead and just switched to a live mercenary, clear the death state
      // so they aren't bounced back to the death screen on subsequent navigation.
      if (user?.isDead) {
        await axios.post('/api/user/reset-death-status', {}, { withCredentials: true });
        setUser((prev: any) => ({ ...prev, isDead: false, deathBattleReport: null }));
      }
    } catch (error) {
      console.error('Failed to fetch mercenary data:', error);
    } finally {
      switchingRef.current = false;
    }
  }, [user, setUser]);

  const createMercenary = useCallback(async (mercenaryData: any) => {
    try {
      const response = await axios.post('/api/mercenary', mercenaryData, { withCredentials: true });
      setMercenaries(prev => [...prev, response.data]);
      switchMercenary(response.data);
    } catch (error) {
      console.error("Failed to create mercenary:", error);
    }
  }, [switchMercenary]);

  const deleteMercenary = useCallback(async (deletedMercenaryId: number) => {
    try {
      await axios.delete(`/api/mercenary/${deletedMercenaryId}`, { withCredentials: true });
      setMercenaries(prev => {
        const filtered = prev.filter(g => g.id !== deletedMercenaryId);
        return filtered;
      });
      // If deleted mercenary was active, switch to first available
      setMercenary((current: any | null) => {
        if (current?.id === deletedMercenaryId) {
          // Will be handled by AuthInitializer or user can select another
          return null;
        }
        return current;
      });
    } catch (error) {
      console.error("Failed to delete mercenary:", error);
    }
  }, []);

  const value = useMemo(() => ({
    mercenaries,
    mercenary,
    mercenaryId,
    fetchUserMercenaries,
    switchMercenary,
    createMercenary,
    deleteMercenary,
    setMercenary,
    setMercenaryId
  }), [mercenaries, mercenary, mercenaryId, fetchUserMercenaries, switchMercenary, createMercenary, deleteMercenary]);

  return (
    <MercenaryContext.Provider value={value}>
      {children}
    </MercenaryContext.Provider>
  );
};

export const useMercenary = () => {
  const context = useContext(MercenaryContext);
  if (!context) {
    throw new Error('useMercenary must be used within a MercenaryProvider');
  }
  return context;
};
